/**
 *
 * InvoiceDetailDetectionsSettings
 *
 */

import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DraggingStyle,
  NotDraggingStyle,
  DropResult,
} from 'react-beautiful-dnd';
import { FormattedMessage } from 'lib/intl';

// api
import {
  getOrderableInvoiceDetailTypesOrder,
  saveOrderableInvoiceDetailTypesOrder,
  InvoiceDetailTypesOrderItem,
  MultiInvoiceDetailTypesOrder,
} from 'lib/api/invoiceDetails';

// ui
import { Flex, Button, buttonClassName, createStyles, withStyles, WithStyles } from '@domonda/ui';
import { getConfigFromStore } from 'lib/api/customerConfig';

const styles = createStyles(({ palette, spacing }) => ({
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: spacing(1, 0),
    borderTop: `1px solid ${palette.border}`,
    borderBottom: `1px solid ${palette.border}`,
    [`& .${buttonClassName}`]: {
      marginLeft: spacing(1),
    },
  },
  droppable: {
    width: '100%',
    padding: spacing(1, 1, 0),
    border: '1px solid #d8d8d8',
  },
  draggable: {
    userSelect: 'none',
    padding: spacing(1),
    marginBottom: spacing(1),
    backgroundColor: '#f9f9fb',
  },
  draggableGroup: {
    padding: 0,
  },
  draggableGroupTitle: {
    padding: spacing(1),
    '&:active ~ $droppable': {
      display: 'none',
    },
  },
}));

const getDroppableStyle = (isDraggingOver: boolean): React.CSSProperties => ({
  backgroundColor: isDraggingOver ? '#cceefa' : '#e6e7eb',
});

const getDraggableStyle = (
  isDragging: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
): React.CSSProperties => ({
  border: isDragging ? '1px dashed #000' : 'none',
  ...draggableStyle,
});

const reorder = (
  list: InvoiceDetailTypesOrderItem[],
  startIndex: number,
  endIndex: number,
): InvoiceDetailTypesOrderItem[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const move = (
  source: InvoiceDetailTypesOrderItem[],
  destination: InvoiceDetailTypesOrderItem[],
  droppableSource: any,
  droppableDestination: any,
) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {};
  (result as any)[droppableSource.droppableId] = sourceClone;
  (result as any)[droppableDestination.droppableId] = destClone;

  return result;
};

interface DraggableProps {
  parentType?: string;
  item: InvoiceDetailTypesOrderItem;
  index: number;
  classes: WithStyles<typeof styles>['classes'];
}

const DraggableItem: React.FC<DraggableProps> = ({ item, index, classes, parentType }) => (
  <Draggable draggableId={`draggable-${item.type}-${parentType}`} index={index}>
    {(dragProvided, dragSnapshot) => (
      <div
        ref={dragProvided.innerRef}
        {...dragProvided.draggableProps}
        {...dragProvided.dragHandleProps}
        className={classes.draggable}
        style={getDraggableStyle(dragSnapshot.isDragging, dragProvided.draggableProps.style)}
      >
        <FormattedMessage defaultMessage={item.type} id={`INVOICE_DETAIL.${item.type}`} />
      </div>
    )}
  </Draggable>
);

const shouldBeShown = (typeName: string) => {
  const typeNameSplit = typeName.split('.');
  if (
    typeNameSplit.length === 2 &&
    (typeNameSplit[0] === 'Client' || typeNameSplit[0] === 'Supplier')
  )
    return true;
  const confi = getConfigFromStore();
  if (typeName === 'Client') return confi.IsClientVisible;
  if (typeName === 'Supplier') return confi.IsSupplierVisible;
  const ite = confi.InvoiceDetails.find((item) => item.Name === typeName);
  if (!ite) return false;
  else return ite.Priority === 'MustHave' || ite.Priority === 'NiceToHave';
};

const DraggableGroup: React.FC<DraggableProps> = ({ item, index, classes }) => (
  <Draggable draggableId={`draggable-${item.type}`} index={index}>
    {(dragProvided, dragSnapshot) => (
      <div
        ref={dragProvided.innerRef}
        {...dragProvided.draggableProps}
        className={clsx(classes.draggable, classes.draggableGroup)}
        style={getDraggableStyle(dragSnapshot.isDragging, dragProvided.draggableProps.style)}
      >
        <div {...dragProvided.dragHandleProps} className={classes.draggableGroupTitle}>
          <FormattedMessage defaultMessage={item.type} id={`INVOICE_DETAIL.${item.type}`} />
        </div>

        <Droppable droppableId={`droppable-${item.type}`} type={`droppable-${item.type}`}>
          {(dropProvided, dropSnapshot) => (
            <div
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
              className={classes.droppable}
              style={getDroppableStyle(dropSnapshot.isDraggingOver)}
            >
              {item.items &&
                item.items.map(
                  (item1, index) =>
                    shouldBeShown(`${item.type}.${item1.type}`) && (
                      <DraggableItem
                        parentType={item.type}
                        key={item1.type}
                        item={item1}
                        index={index}
                        classes={classes}
                      />
                    ),
                )}

              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    )}
  </Draggable>
);

export interface InvoiceDetailDetectionsSettingsProps {}

const InvoiceDetailDetectionsSettings: React.FC<
  InvoiceDetailDetectionsSettingsProps & WithStyles<typeof styles>
> = ({ classes }) => {
  const [outerEl, setOuterEl] = useState<HTMLElement | null>(null);
  const [minHeight, setMinHeight] = useState<string | number>('auto');
  const [orderItems, setOrderItems] = useState<MultiInvoiceDetailTypesOrder>(
    getOrderableInvoiceDetailTypesOrder,
  );
  const [oldOrder, setOldOrder] = useState<MultiInvoiceDetailTypesOrder>([[], []]);
  const conf = getConfigFromStore();
  const removeGuiSplitArea = conf ? conf.RemoveGuiSplittingArea : false;

  const initialReorder = (initialOrder: MultiInvoiceDetailTypesOrder) => {
    const orderCopy = [...initialOrder];
    const newOrder: MultiInvoiceDetailTypesOrder = [[], []];
    orderCopy.forEach((items, index) => {
      items.forEach((element) => {
        if (shouldBeShown(element.type)) newOrder[index].push(element);
      });
    });
    // orderCopy.forEach((items, index) => {
    //   items.forEach((element) => {
    //     if (!shouldBeShown(element.type)) newOrder[index].push(element);
    //   });
    // });
    return newOrder;
  };

  const finalReorder = () => {
    const newOrder: MultiInvoiceDetailTypesOrder = [[], []];
    orderItems.forEach((items, index) => {
      items.forEach((element) => {
        newOrder[index].push(element);
      });
    });
    oldOrder.forEach((items, index) => {
      items.forEach((element) => {
        if (!shouldBeShown(element.type)) newOrder[index].push(element);
      });
    });
    return newOrder;
  };

  useEffect(() => {
    if (!removeGuiSplitArea) {
      let newOrder: InvoiceDetailTypesOrderItem[] = [];
      newOrder = newOrder.concat(orderItems[0]);
      newOrder = newOrder.concat(orderItems[1]);
      setOldOrder([newOrder, []]);
      setOrderItems(initialReorder([newOrder, []]));
      // setOrderItems([newOrder, []]);
    } else {
      setOldOrder(orderItems);
      setOrderItems(initialReorder(orderItems));
    }
  }, []);
  const [orderChanged, setOrderChanged] = useState(false);

  const saveOrderItems = () => {
    // saveOrderableInvoiceDetailTypesOrder(orderItems);
    saveOrderableInvoiceDetailTypesOrder(finalReorder());
    setOrderChanged(false);
  };

  const restoreOrderItems = () => {
    const order = getOrderableInvoiceDetailTypesOrder();
    setOldOrder(order);
    setOrderItems(initialReorder(order));
    // setOrderItems(getOrderableInvoiceDetailTypesOrder());
    setOrderChanged(false);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId === destination.droppableId && result.type === 'droppable-0') {
      // items = reorder(items, result.source.index, result.destination.index);
      const ind = Number(source.droppableId.split('-')[1]);
      const items = reorder(orderItems[ind], result.source.index, result.destination.index);
      if (source.droppableId === 'droppable-0') {
        setOrderItems([items, orderItems[1]]);
        setOrderChanged(!isEqual([items, orderItems[1]], getOrderableInvoiceDetailTypesOrder()));
      } else {
        setOrderItems([orderItems[0], items]);
        setOrderChanged(!isEqual([orderItems[0], items], getOrderableInvoiceDetailTypesOrder()));
      }
    } else if (result.type === 'droppable-0') {
      const src = Number(source.droppableId.split('-')[1]);
      const dest = Number(destination.droppableId.split('-')[1]);
      // const result = move(OI[source.droppableId], OI[destination.droppableId], source, destination);
      const result = move(orderItems[src], orderItems[dest], source, destination);
      setOrderItems([(result as any)['droppable-0'], (result as any)['droppable-1']]);
      setOrderChanged(
        !isEqual(
          [(result as any)['droppable-0'], (result as any)['droppable-1']],
          getOrderableInvoiceDetailTypesOrder(),
        ),
      );
      //setOI({ 'droppable-0': result['droppable-0'], 'droppable-1': result['droppable-1'] });
    } else {
      const items = [orderItems[0], orderItems[1]];
      const group = orderItems[0].find((item) => `droppable-${item.type}` === result.type);
      const group1 = orderItems[1].find((item) => `droppable-${item.type}` === result.type);

      if (group) {
        const groupIndex = orderItems[0].indexOf(group);
        const groupUpdated = Object.assign(
          {},
          { ...group },
          group.items
            ? { items: reorder(group.items, result.source.index, result.destination.index) }
            : {},
        );

        items[0][groupIndex] = groupUpdated;
      } else if (group1) {
        const groupIndex = orderItems[1].indexOf(group1);
        const groupUpdated = Object.assign(
          {},
          { ...group1 },
          group1.items
            ? { items: reorder(group1.items, result.source.index, result.destination.index) }
            : {},
        );

        items[1][groupIndex] = groupUpdated;
      }
      setOrderItems(items);
      setOrderChanged(!isEqual(items, getOrderableInvoiceDetailTypesOrder()));
    }

    //  setOrderItems(items);
    // setOrderChanged(!isEqual(orderItems, getOrderableInvoiceDetailTypesOrder()));
  };

  useEffect(() => {
    if (outerEl)
      // Remove top and bottom padding from calculation (6px).
      setMinHeight(outerEl.offsetHeight - 6 * 2);
  }, [outerEl]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Flex container direction="column" spacing={1}>
        <Flex item>
          <div className={classes.actions}>
            <Button disabled={!orderChanged} variant="contained" onClick={restoreOrderItems}>
              <FormattedMessage id="RESTORE" />
            </Button>

            <Button
              color="secondary"
              disabled={!orderChanged}
              variant="contained"
              onClick={saveOrderItems}
            >
              <FormattedMessage id="SAVE" />
            </Button>
          </div>
        </Flex>

        <Flex item ref={setOuterEl}>
          <Flex container direction="row" spacing={3}>
            <Flex item flex={1}>
              <Droppable droppableId="droppable-0" type="droppable-0">
                {(dropProvided, dropSnapshot) => (
                  <div
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                    className={classes.droppable}
                    style={{
                      ...getDroppableStyle(dropSnapshot.isDraggingOver),
                      minHeight,
                    }}
                  >
                    {/*{orderItems.map((item: InvoiceDetailTypesOrderItem, index) =>*/}
                    {/* {OI['droppable-0'].map((item: InvoiceDetailTypesOrderItem, index) => */}
                    {orderItems[0].map((item: InvoiceDetailTypesOrderItem, index) =>
                      !shouldBeShown(item.type) ? null : item.items === null ? (
                        <DraggableItem
                          key={`${item.type}-0`}
                          item={item}
                          index={index}
                          classes={classes}
                        />
                      ) : (
                        <DraggableGroup
                          key={item.type}
                          item={item}
                          index={index}
                          classes={classes}
                        />
                      ),
                    )}

                    {dropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </Flex>
            {orderItems[1] && removeGuiSplitArea && (
              <Flex item flex={1}>
                <Droppable droppableId="droppable-1" type="droppable-0">
                  {(dropProvided, dropSnapshot) => (
                    <div
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                      className={classes.droppable}
                      style={{
                        ...getDroppableStyle(dropSnapshot.isDraggingOver),
                        minHeight,
                      }}
                    >
                      {/* {orderItems.map((item: InvoiceDetailTypesOrderItem, index) =>*/}
                      {/* {OI['droppable-1'].map((item: InvoiceDetailTypesOrderItem, index) => */}
                      {orderItems[1].map((item: InvoiceDetailTypesOrderItem, index) =>
                        !shouldBeShown(item.type) ? null : item.items === null ? (
                          <DraggableItem
                            key={`${item.type}-1`}
                            item={item}
                            index={index}
                            classes={classes}
                          />
                        ) : (
                          <DraggableGroup
                            key={item.type}
                            item={item}
                            index={index}
                            classes={classes}
                          />
                        ),
                      )}

                      {dropProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </DragDropContext>
  );
};

const StyledInvoiceDetailDetectionsSettings = withStyles(styles)(InvoiceDetailDetectionsSettings);
export { StyledInvoiceDetailDetectionsSettings as InvoiceDetailDetectionsSettings };
